import { type Dispatch, type SetStateAction, useCallback } from 'react'

import type { Row } from '@tanstack/react-table'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { loadCreatorsFx } from '@/models/creators.ts'
import { creatorService } from '@/modules/creators/service/creator-service.ts'
import { SegmentEvents, analytics } from '@/utils/analytics'
import { handleError } from '@/utils/error'

interface TeamRemoveDialogType {
  row: Row<any> // Todo
  showRemoveDialog: boolean
  setShowRemoveDialog: Dispatch<SetStateAction<boolean>>
}

export const RemoveCreatorDialog = ({
  row,
  showRemoveDialog,
  setShowRemoveDialog,
}: TeamRemoveDialogType) => {
  const queryClient = useQueryClient()
  const creator = row.original
  const toast = useToast()
  const handleSubmit = useCallback(
    () => {
      setShowRemoveDialog(false)
      deleteMutation
        .mutateAsync(creator.id)
        .then(() => {
          analytics.track(SegmentEvents.CreatorDeleted)
          toast.success(`Creator ${creator.name} has been deleted.`)
        })
        .catch((error) => {
          handleError(
            error,
            'Unknown error while deleting creator. Please contact support.',
          )
        })
        .finally(() => {
          loadCreatorsFx()
          queryClient.invalidateQueries('licenses')
          queryClient.invalidateQueries('creator')
          deleteMutation.reset()
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const closeDialog = () => {
    setShowRemoveDialog(false)
  }
  const deleteMutation = useMutation((creatorId: number) => {
    return creatorService.deleteCreator(creatorId)
  }, {})

  return (
    <Dialog
      show={showRemoveDialog}
      onClose={closeDialog}
    >
      <Dialog.Header>
        <Dialog.Title>Delete Creator</Dialog.Title>
        <Dialog.Description className="text-center">
          Are you sure you want to delete the creator{' '}
          <span className="text-title">{row.original.name}</span>? All data,
          settings etc. will be <span className="text-title">permanently</span>{' '}
          lost and we can&#39;t recover it.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Footer className="mt-6">
        <Button
          variant="outline"
          onClick={closeDialog}
          className="w-full"
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={handleSubmit}
          className="w-full"
        >
          Delete
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
